import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReactTooltip from "react-tooltip";
import classnames from "classnames";
import { Multiselect } from "multiselect-react-dropdown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getAllMetadata } from "store/actions/metadataAction";
import { getAllDesktopUsers, getAllWebUsers } from "store/actions/userAction";
import { getAllClients } from "store/actions/clientAction";
import { getAllDepartments } from "store/actions/departmentAction";
import Translate from "helpers/Translate";
import debounce from "components/Forms/utils/debounce";
import * as fileStatuses from "helpers/fileStatus.json";

// Styles
import "./Filters.styles.scss";

const MULTISELECT_STYLE = {
  multiselectContainer: {
    width: "100%"
  },
  searchBox: {
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    minHeight: "40px",
    paddingLeft: "36px",
    paddingRight: "32px",
    paddingTop: "6px",
    paddingBottom: "6px",
    background: "#ffffff",
    boxShadow: "none",
    alignItems: "center"
  },
  inputField: {
    margin: "0",
    padding: "0",
    border: "none",
    fontSize: "0.8rem",
    color: "#1a202c"
  },
  chips: {
    margin: "0 4px 0 0",
    fontSize: "0.72rem"
  },
  optionContainer: {
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    marginTop: "4px",
    zIndex: 30
  }
};

function IconInput({ icon, className, children, showChevron }) {
  return (
    <div className={classnames("fh-input", className)}>
      <i className={classnames("fh-input__icon", icon)} aria-hidden="true"></i>
      {children}
      {showChevron && (
        <i
          className="fas fa-chevron-down fh-input__chevron"
          aria-hidden="true"
        ></i>
      )}
    </div>
  );
}

function DateFilterField({ label, selected, onChange, onClear, placeholder }) {
  return (
    <div className="fh-date">
      <label className="fh-date__label">{label}</label>
      <div className="fh-date__input">
        <DatePicker
          className="fh-date__control"
          dateFormat="dd/MM/yyyy"
          selected={selected}
          onChange={onChange}
          placeholderText={placeholder}
        />
        {selected && (
          <button
            type="button"
            className="fh-date__clear"
            onClick={onClear}
            aria-label="Clear date"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        )}
        <i className="far fa-calendar fh-date__calendar" aria-hidden="true"></i>
      </div>
    </div>
  );
}

function Filters({
  handleFilterData,
  handleResetFilter,
  selectedFileId,
  setSelectedFileId,
  selectedOriginalFilename,
  setSelectedOriginalFilename,
  selectedMetadataNames,
  setSelectedMetadataNames,
  selectedDesktopUsernames,
  setSelectedDesktopUsernames,
  selectedWebUsernames,
  setSelectedWebUsernames,
  selectedClients,
  setSelectedClients,
  selectedDepartments,
  setSelectedDepartments,
  selectedCollectionItemValues,
  setSelectedCollectionItemValues,

  selectedDataEntryStartDate,
  selectedDataEntryEndDate,
  setSelectedDataEntryStartDate,
  setSelectedDataEntryEndDate,

  selectedWebUserApprovalStartDate,
  selectedWebUserApprovalEndDate,
  setSelectedWebUserApprovalStartDate,
  setSelectedWebUserApprovalEndDate,

  selectedDmsUploadStartDate,
  selectedDmsUploadEndDate,
  setSelectedDmsUploadStartDate,
  setSelectedDmsUploadEndDate,

  selectedStatus,
  setSelectedStatus,
  selectedPageLimit,
  setSelectedPageLimit
}) {
  const dispatch = useDispatch();
  const isLoadingMetadata = useSelector(
    state => state.metadata.isLoadingMetadata
  );
  const allMetadata = useSelector(state => state.metadata.getAllMetadata);

  const isLoadingUser = useSelector(state => state.user.isLoadingUser);
  const allDesktopUsers = useSelector(state => state.user.getAllDesktopUsers);
  const allWebUsers = useSelector(state => state.user.getAllWebUsers);
  const allClients = useSelector(state => state.client.getAllClients);
  const isLoadingClient = useSelector(state => state.client.isLoadingClient);
  const isLoadingDepartment = useSelector(
    state => state.department.isLoadingDepartment
  );
  const allDepartments = useSelector(
    state => state.department.getAllDepartments
  );

  const [shake, setShake] = useState(false);

  useEffect(() => {
    dispatch(getAllClients({}));
    dispatch(getAllDesktopUsers({}));
    dispatch(getAllWebUsers({}));
    dispatch(getAllDepartments({}));
    dispatch(getAllMetadata({}));
  }, [dispatch]);

  useEffect(() => {
    const shouldShake =
      !selectedWebUsernames.length &&
      (selectedWebUserApprovalStartDate || selectedWebUserApprovalEndDate)
        ? true
        : false;

    setShake(shouldShake);
  }, [
    selectedWebUsernames,
    selectedWebUserApprovalStartDate,
    selectedWebUserApprovalEndDate
  ]);

  const handleSearchMetadataInput = useCallback(
    debounce(title => {
      dispatch(getAllMetadata({ title }));
    }, 500),
    []
  );

  const handleSearchClientInput = useCallback(
    debounce(name => {
      dispatch(getAllClients({ name }));
    }, 500),
    []
  );

  const handleSearchDesktopUserInput = useCallback(
    debounce(name => {
      dispatch(getAllDesktopUsers({ name }));
    }, 500),
    []
  );

  const handleSearchWebUserInput = useCallback(
    debounce(name => {
      dispatch(getAllWebUsers({ name }));
    }, 500),
    []
  );

  const handleSearchCollectionInput = useCallback(
    debounce(name => {
      dispatch(getAllDepartments({ name }));
    }, 500),
    []
  );

  const activeFilterCount = [
    selectedFileId,
    selectedOriginalFilename,
    selectedMetadataNames?.length,
    selectedClients?.length,
    selectedDesktopUsernames?.length,
    selectedWebUsernames?.length,
    selectedDepartments?.length,
    selectedCollectionItemValues?.length,
    selectedDataEntryStartDate,
    selectedDataEntryEndDate,
    selectedWebUserApprovalStartDate,
    selectedWebUserApprovalEndDate,
    selectedDmsUploadStartDate,
    selectedDmsUploadEndDate,
    selectedStatus && selectedStatus !== fileStatuses.STATUS_SENT
      ? selectedStatus
      : "",
    selectedPageLimit && String(selectedPageLimit) !== "10"
      ? selectedPageLimit
      : ""
  ].filter(Boolean).length;

  const selectDateLabel = Translate({ id: "select_date" });

  return (
    <div className="file-history-filters-card">
      <ReactTooltip />

      <div className="file-history-filters-card__header">
        <div className="file-history-filters-card__title">
          <i className="fas fa-sliders-h" aria-hidden="true"></i>
          <span>{Translate({ id: "filters" })}</span>
          {activeFilterCount > 0 && (
            <span className="file-history-filters-card__badge">
              {activeFilterCount} {Translate({ id: "active" })}
            </span>
          )}
        </div>
      </div>

      <div className="file-history-filters-card__grid">
        <IconInput icon="far fa-file-alt">
          <input
            type="text"
            className="fh-input__control"
            data-tip={Translate({ id: "file_id" })}
            placeholder={Translate({ id: "enter_file_id" })}
            value={selectedFileId}
            onChange={e => setSelectedFileId(e.target.value.trim())}
          />
        </IconInput>

        <IconInput icon="far fa-file">
          <input
            type="text"
            className="fh-input__control"
            data-tip={Translate({ id: "original_filename" })}
            placeholder={Translate({ id: "enter_original_filename" })}
            value={selectedOriginalFilename}
            onChange={e => setSelectedOriginalFilename(e.target.value.trim())}
          />
        </IconInput>

        <IconInput
          icon="fas fa-layer-group"
          className="fh-input--multiselect"
          showChevron
        >
          <Multiselect
            style={MULTISELECT_STYLE}
            id="document_metadata_type"
            loading={isLoadingMetadata}
            placeholder={Translate({ id: "select_document_type" })}
            selectedValues={selectedMetadataNames}
            options={allMetadata?.data || []}
            onSelect={list => {
              setSelectedMetadataNames(list);
              dispatch(getAllMetadata({}));
            }}
            onRemove={list => {
              setSelectedMetadataNames(list);
            }}
            onSearch={title => handleSearchMetadataInput(title)}
            displayValue="title"
          />
        </IconInput>

        <IconInput
          icon="far fa-user"
          className="fh-input--multiselect"
          showChevron
        >
          <Multiselect
            style={MULTISELECT_STYLE}
            id="client_name"
            loading={isLoadingClient}
            placeholder={Translate({ id: "select_client" })}
            selectedValues={selectedClients}
            options={allClients?.data?.map(client => ({
              id: client.id,
              name: client.name
            }))}
            onSelect={list => {
              setSelectedClients(list);
              dispatch(getAllClients({}));
            }}
            onRemove={list => {
              setSelectedClients(list);
            }}
            onSearch={name => handleSearchClientInput(name)}
            displayValue="name"
          />
        </IconInput>

        <IconInput
          icon="fas fa-desktop"
          className="fh-input--multiselect"
          showChevron
        >
          <Multiselect
            style={MULTISELECT_STYLE}
            id="desktop_user"
            loading={isLoadingUser}
            placeholder={Translate({ id: "select_desktop_user" })}
            selectedValues={selectedDesktopUsernames}
            options={allDesktopUsers?.map(user => ({
              id: user.id,
              name: `${user.fullname} (${user.email})`
            }))}
            onSelect={list => {
              setSelectedDesktopUsernames(list);
              dispatch(getAllDesktopUsers({}));
            }}
            onRemove={list => {
              setSelectedDesktopUsernames(list);
            }}
            onSearch={name => handleSearchDesktopUserInput(name)}
            displayValue="name"
          />
        </IconInput>

        <div
          className={classnames({
            "file-history__filters-shake": shake
          })}
        >
          <IconInput
            icon="fas fa-user-friends"
            className="fh-input--multiselect"
            showChevron
          >
            <Multiselect
              style={MULTISELECT_STYLE}
              id="web_console_user"
              loading={isLoadingUser}
              placeholder={Translate({ id: "select_web_user" })}
              selectedValues={selectedWebUsernames}
              options={allWebUsers?.map(user => ({
                id: user.id,
                name: `${user.fullname} (${user.email})`
              }))}
              onSelect={list => {
                setSelectedWebUsernames(list);
                dispatch(getAllWebUsers({}));
              }}
              onRemove={list => {
                setSelectedWebUsernames(list);
              }}
              onSearch={name => handleSearchWebUserInput(name)}
              displayValue="name"
            />
          </IconInput>
          {shake && (
            <div className="help form-error">
              {Translate({ id: "please_select_at_least_one_option" })}
            </div>
          )}
        </div>

        <IconInput
          icon="far fa-folder"
          className="fh-input--multiselect"
          showChevron
        >
          <Multiselect
            style={MULTISELECT_STYLE}
            id="department"
            loading={isLoadingDepartment}
            placeholder={Translate({ id: "select_collection" })}
            selectedValues={selectedDepartments}
            options={allDepartments?.data?.map(department => ({
              id: department.id,
              name: department.name,
              items: department.items
            }))}
            onSelect={list => {
              setSelectedDepartments(list);
              dispatch(getAllWebUsers({}));
            }}
            onRemove={list => {
              setSelectedDepartments(list);
            }}
            onSearch={name => handleSearchCollectionInput(name)}
            displayValue="name"
          />
        </IconInput>

        <IconInput
          icon="fas fa-tags"
          className="fh-input--multiselect"
          showChevron
        >
          <Multiselect
            style={MULTISELECT_STYLE}
            id="department_item_value"
            loading={isLoadingDepartment}
            placeholder={Translate({ id: "select_item_value" })}
            selectedValues={selectedCollectionItemValues}
            options={selectedDepartments?.flatMap(department => {
              return department?.items.map(departmentItem => ({
                value: departmentItem.value,
                label: departmentItem.value
              }));
            })}
            onSelect={list => {
              setSelectedCollectionItemValues(list);
              dispatch(getAllWebUsers({}));
            }}
            onRemove={list => {
              setSelectedCollectionItemValues(list);
            }}
            displayValue="label"
          />
        </IconInput>
      </div>

      <div className="file-history-filters-card__dates">
        <DateFilterField
          label={Translate({
            id: "start_date_of",
            values: [Translate({ id: "data_entry" })]
          })}
          selected={selectedDataEntryStartDate}
          onChange={date => setSelectedDataEntryStartDate(date)}
          onClear={() => setSelectedDataEntryStartDate(null)}
          placeholder={selectDateLabel}
        />

        <DateFilterField
          label={Translate({
            id: "end_date_of",
            values: [Translate({ id: "data_entry" })]
          })}
          selected={selectedDataEntryEndDate}
          onChange={date => setSelectedDataEntryEndDate(date)}
          onClear={() => setSelectedDataEntryEndDate(null)}
          placeholder={selectDateLabel}
        />

        <DateFilterField
          label={Translate({
            id: "start_date_of",
            values: [Translate({ id: "web_user_approval" })]
          })}
          selected={selectedWebUserApprovalStartDate}
          onChange={date => setSelectedWebUserApprovalStartDate(date)}
          onClear={() => setSelectedWebUserApprovalStartDate(null)}
          placeholder={selectDateLabel}
        />

        <DateFilterField
          label={Translate({
            id: "end_date_of",
            values: [Translate({ id: "web_user_approval" })]
          })}
          selected={selectedWebUserApprovalEndDate}
          onChange={date => setSelectedWebUserApprovalEndDate(date)}
          onClear={() => setSelectedWebUserApprovalEndDate(null)}
          placeholder={selectDateLabel}
        />

        <DateFilterField
          label={Translate({
            id: "start_date_of",
            values: [Translate({ id: "dms_upload" })]
          })}
          selected={selectedDmsUploadStartDate}
          onChange={date => setSelectedDmsUploadStartDate(date)}
          onClear={() => setSelectedDmsUploadStartDate(null)}
          placeholder={selectDateLabel}
        />

        <DateFilterField
          label={Translate({
            id: "end_date_of",
            values: [Translate({ id: "dms_upload" })]
          })}
          selected={selectedDmsUploadEndDate}
          onChange={date => setSelectedDmsUploadEndDate(date)}
          onClear={() => setSelectedDmsUploadEndDate(null)}
          placeholder={selectDateLabel}
        />
      </div>

      <div className="file-history-filters-card__secondary">
        <IconInput
          icon="fas fa-info-circle"
          className="fh-input--select"
          showChevron
        >
          <select
            className="fh-input__control"
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            data-tip={Translate({ id: "status" })}
          >
            <option value="">{Translate({ id: "select_all" })}</option>
            {Object.keys(fileStatuses.default).map((value, idx) => (
              <option key={idx} value={fileStatuses.default[value]}>
                {fileStatuses.default[value].replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </IconInput>

        <IconInput
          icon="fas fa-list-ol"
          className="fh-input--select"
          showChevron
        >
          <select
            className="fh-input__control"
            onChange={e => setSelectedPageLimit(e.target.value)}
            data-tip={Translate({ id: "records_per_page" })}
            value={selectedPageLimit}
          >
            {[10, 20, 30, 40, 50, 100].map(limit => (
              <option key={limit} value={limit}>
                {Translate({ id: "records_per_page" })} ({limit})
              </option>
            ))}
          </select>
        </IconInput>
      </div>

      <div className="file-history-filters-card__footer">
        <button
          type="button"
          className="btn file-history-filters-card__btn-reset"
          onClick={handleResetFilter}
        >
          <i className="fas fa-redo mr-10"></i>
          {Translate({ id: "reset_filter" })}
        </button>
        <button
          type="button"
          className="btn btn-primary file-history-filters-card__btn-apply"
          onClick={handleFilterData}
        >
          <i className="fas fa-filter mr-10"></i>
          {Translate({ id: "apply_filters" })}
        </button>
      </div>
    </div>
  );
}

export default Filters;
